import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { useCartContext } from '@/contexts/cartContext';
import { useTranslation } from 'react-i18next';
import { useDebouncedValue } from '@/hooks/debounce';
import { formatPrice, getPriceForCurrency } from '@/utils/prices';
import { IconCheck, IconReceipt2, IconTag, IconX, IconTrees } from '@tabler/icons-react';
import Delayed from '@/components/commons/delayed';
import { motion } from 'framer-motion';
import { fadeIn, popFromBottom } from '@/utils/animations';

export default function CartMenu() {
  const { t } = useTranslation();
  const [couponCode, setCouponCode] = useState('');
  const debouncedCouponCode = useDebouncedValue(couponCode, 500);
  const {
    prices,
    vatRate,
    vatAmount,
    totalIncludingVat,
    totalWithoutVat,
    isVatNumberValid,
    couponAmount,
    coupon,
    currency,
    sponsor,
    setCartPartialState,
  } = useCartContext();

  useEffect(() => {
    setCartPartialState({
      couponCode: debouncedCouponCode,
    });
  }, [debouncedCouponCode, setCartPartialState]);

  return (
    <div className="w-full lg:w-[400px] lg:sticky lg:top-8 lg:self-start">
      <h3 className="text-xl font-bold mb-6 flex items-center">
        <IconReceipt2 className="mr-2" />
        {t('Payment_OrderSummary')}
      </h3>
      <div className="space-y-4">
        {prices.map((price, index) => (
          <motion.div
            {...popFromBottom}
            transition={{ delay: index * 0.1 }}
            key={index}
            className="flex justify-between items-center border border-border bg-muted p-2 rounded-lg gap-1"
          >
            <div className="flex items-center">
              <img
                loading="lazy"
                src={
                  price?.product?.images && price?.product.images?.length > 0
                    ? price?.product.images[0]
                    : `/images/prices/${price.id}.png`
                }
                alt={price.id}
                onError={e => (e.currentTarget.src = '/images/prices/archireport_pro_yearly.png')}
                className="w-12 h-12 object-cover rounded mr-4"
              />
              <span>
                {price.metadata?.translation && t(price.metadata.translation) !== price.metadata.translation
                  ? t(price.metadata.translation)
                  : price.nickname}
              </span>
            </div>
            <span className="font-semibold">{formatPrice(getPriceForCurrency(price, currency), currency)}</span>
          </motion.div>
        ))}

        {couponAmount > 0 && (
          <motion.div
            {...popFromBottom}
            className="flex justify-between items-center border border-border bg-muted p-2 rounded-lg gap-1 text-green-600 dark:text-green-400"
          >
            <div className="flex items-center min-h-12">
              <IconTag className="h-12 ml-4 mr-6" width={20} />
              <span>{`${t('payment_coupon')} ${coupon?.percent_off ? `(- ${coupon?.percent_off}%)` : ''} `}</span>
            </div>
            <span className="font-semibold">- {formatPrice(couponAmount, currency)}</span>
          </motion.div>
        )}

        {sponsor && sponsor.id && (
          <motion.div
            {...popFromBottom}
            className="flex justify-between items-center border border-border bg-muted p-2 rounded-lg gap-1 text-green-600 dark:text-green-400"
          >
            <div className="flex items-center min-h-12">
              <IconTrees className="h-12 ml-4 mr-6" width={30} />
              <span>
                Grâce à ce parrainage, un arbre vous sera offert sur{' '}
                <a href="https://ecotree.green" target="_blank" className="underline">
                  ecotree.green
                </a>
              </span>
            </div>
            <span className="font-semibold"></span>
          </motion.div>
        )}
      </div>
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex justify-between items-center mb-2">
          <span>{`${t('Payment_SubTotal')} (${t('Web_Sublife_NoTax')})`}</span>
          <span>{formatPrice(totalWithoutVat, currency)}</span>
        </div>
        <div
          className={`flex justify-between items-center mb-2 ${
            isVatNumberValid === 'valid' ? 'line-through opacity-60' : ''
          }`}
        >
          <span className="flex items-center">
            {t('Web_Sublife_TaxLabel')} ({(vatRate * 100).toFixed(1)}%)
          </span>
          <span>{formatPrice(vatAmount, currency)}</span>
        </div>

        <div className="flex space-x-2 mb-4 items-center justify-end">
          {debouncedCouponCode != '' && (
            <div className="flex-1">
              {coupon && couponAmount > 0 && (
                <Delayed waitBeforeShow={400}>
                  <motion.div {...fadeIn}>
                    <IconCheck className="text-green-500 text-xl" />
                  </motion.div>
                </Delayed>
              )}
              {coupon && couponAmount === 0 && (
                <Delayed waitBeforeShow={400}>
                  <motion.div {...fadeIn}>
                    <IconX className="text-red-500 text-xl" />
                  </motion.div>
                </Delayed>
              )}
            </div>
          )}
          <Input
            className="flex-10 text-right items-end"
            id="coupon"
            onChange={e => setCouponCode(e.target.value)}
            placeholder={t('Payment_CouponCode')}
          />
        </div>

        <div className="flex justify-between items-center font-bold text-lg mt-4 pt-4 border-t border-gray-200">
          <span>
            {t('Web_Sublife_Total')} ({t('Web_Sublife_FullTax')})
          </span>
          <span>{formatPrice(totalIncludingVat, currency)}</span>
        </div>
      </div>
    </div>
  );
}
